import { LIQUIDATION_RATE } from '@/constants';
import { TransactionHistoryItem } from '@/context/types';
import { VaultTransaction } from '@/hooks/api/types';
import { ValidatorLiquidatedVault, ValidatorLiqVaultResponse } from '@/hooks/api/useQueryLiquidatedVaults';
import { getHealthFactor } from '@/utils/getHealthFactor';
import { BaseUtxo, VaultPrevout, VaultProfile, VaultReturnData, VaultToken, VaultWallet } from '@ducat-unit/client-sdk';

export type Action = 'w' | 'b' | 'd' | 'r' | 'o' | 'l' | 'x';
export type ActionType = 'withdraw' | 'borrow' | 'deposit' | 'repay' | 'create' | 'liquidation' | 'repossess';

export const ACTION_TO_TYPE: Record<Action, ActionType> = {
  w: 'withdraw',
  b: 'borrow',
  d: 'deposit',
  r: 'repay',
  o: 'create',
  l: 'liquidation',
  x: 'repossess',
};

export interface VaultChartData {
  eventAmount: number;
  amount: number;
  unit: string;
  type: ActionType;
  date: string;
  btcWallet: number;
  txId: string;
  oraclePrice: number;
}

export function transformVaultHistoryForChart(receivedData: VaultTransaction[]): VaultChartData[] {
  const sortedData = [...receivedData].sort((a, b) => a.timestamp - b.timestamp);

  return sortedData.map((item) => {
    // Convert action to type
    const type = ACTION_TO_TYPE[item.action];

    // Determine unit based on action
    const unit = item.action === 'w' || item.action === 'd' ? 'BTC' : 'UNIT';

    // Calculate eventAmount
    let eventAmount = 0;
    if (unit === 'BTC') {
      eventAmount = item.btc_amt / 100_000_000; // Convert from satoshis
    } else if (type === 'create') {
      eventAmount = item.btc_amt / 100_000_000; // Use BTC amount for create
    } else {
      eventAmount = item.unit_amt / 100; // Convert from cents
    }

    // Calculate amount (current vault balance)
    const amount = item.amount_borrowed / 100; // Convert from cents

    // Calculate btcWallet (vault's BTC balance)
    const btcWallet = item.vault_amount / 100_000_000; // Convert from satoshis

    return {
      eventAmount,
      amount,
      unit,
      type,
      date: new Date(item.timestamp * 1000).toISOString(),
      btcWallet,
      txId: item.transaction_id,
      oraclePrice: item.oracle_price, // Adding oracle price to the chart data
    };
  });
}

export function transformVaultHistoryForTable(receivedData: VaultTransaction[]): TransactionHistoryItem[] {
  const sortedData = [...receivedData].sort((a, b) => a.timestamp - b.timestamp);

  let previousItem: VaultTransaction;
  const transformedData = sortedData.map((item) => {
    const currentHealthFactor = item.vault_amount
      ? ((item.oracle_price * (item.vault_amount / 100_000_000)) / (item.amount_borrowed / 100)) * 100
      : 1;
    const previousHealthFactor = previousItem
      ? ((item.oracle_price * (previousItem.vault_amount / 100_000_000)) / (previousItem.amount_borrowed / 100)) * 100
      : 1;

    const sanitizedPreviousHealthFactor = !Number.isFinite(previousHealthFactor) ? 1 : previousHealthFactor;
    const sanitizedCurrentHealthFactor = !Number.isFinite(currentHealthFactor) ? 1 : currentHealthFactor;

    const transformedItem = {
      date: new Date(item.timestamp * 1000).toISOString(),
      type: ACTION_TO_TYPE[item.action],
      amount: item.action === 'w' || item.action === 'd' ? item.btc_amt / 100_000_000 : item.unit_amt / 100,
      amountUnit: item.action === 'w' || item.action === 'd' ? 'BTC' : 'UNIT',
      status: 'Completed',
      transactionId: item.transaction_id,
      healthFactorBefore: item.action === 'o' ? null : Number(sanitizedPreviousHealthFactor?.toFixed(0)),
      healthFactorAfter: Number(currentHealthFactor?.toFixed(0)),
      healthStatusBefore: getHealthFactor(sanitizedPreviousHealthFactor),
      healthStatusAfter: getHealthFactor(sanitizedCurrentHealthFactor),
      bitcoinPrice: item.oracle_price,
      unitInVault: item.unit_amt ? item.unit_amt / 100 : 0,
      btcInVault: item.vault_amount ? item.vault_amount / 100_000_000 : 0,
    } as TransactionHistoryItem;

    previousItem = item;

    return transformedItem;
  });
  return transformedData;
}

export function isBatchAllowed(wallet: VaultWallet) {
  const address = wallet.acct.sats.address;
  // Allow batch signing only for native segwit addr for now.
  return /^tb1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39,59}$/.test(address.toLowerCase());
}

export function computeLiquidationPrice(unitInVault: number, btcInVault: number) {
  const tholdPrice = (unitInVault * LIQUIDATION_RATE) / btcInVault || 0;
  return Math.floor(tholdPrice * 100) / 100;
}

export function computeVaultUtxoFromTx(tx: VaultTransaction): VaultPrevout {
  const rdata: VaultReturnData = {
    is_locked: false,
    thold_hash: tx.liquidation_hash,
    thold_price: tx.liquidation_threshold,
    unit_balance: tx.amount_borrowed,
    unit_price: tx.oracle_price,
    unit_stamp: tx.timestamp,
    vault_action: tx.action,
  };
  const [_, vout] = tx.utxo.split(':');
  const utxo: BaseUtxo = {
    value: tx.vault_amount,
    script: tx.utxo_script,
    txid: tx.transaction_id,
    vout: Number(vout),
  };

  return {
    rdata,
    utxo,
  };
}

export function computeUnitBalance(profile: VaultProfile | undefined, vaultHistoryTxs: VaultTransaction[] | undefined) {
  if (!profile || !profile.utxo || !profile.rdata) {
    return 0;
  }
  const profileTxid = profile.utxo.txid;
  if (!vaultHistoryTxs?.[0] || vaultHistoryTxs?.[0]?.transaction_id === profileTxid) {
    return profile.rdata.unit_balance;
  }
  return vaultHistoryTxs[0].amount_borrowed;
}

export function isLiquidatedVault(vaultPubkey: string, liquidatedVaults: ValidatorLiquidatedVault[] | undefined) {
  const isLiquidated = liquidatedVaults && liquidatedVaults?.some((v) => v.vault_pubkey === vaultPubkey);
  return !!isLiquidated;
}
